/**
 * Attendance response shapes. Mirrors the server DTOs by hand — see api-types.ts for why.
 */

export const DATE_PRESETS = [
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "last_7_days",
  "last_30_days",
  "custom",
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

/** Labels for the filter control. The server owns the actual date maths. */
export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  custom: "Custom range",
};

export interface ResolvedRange {
  from: string;
  to: string;
  label: string;
}

export interface EmployeeRef {
  id: string;
  employeeNumber: string;
  fullName: string;
  department: { id: string; code: string; name: string } | null;
  /**
   * Which business the person belongs to.
   *
   * Present on a day row because employee numbers are unique per company, not
   * across the group — `YG-00004` names two different people — so on any view
   * that spans companies the number alone is ambiguous.
   */
  company?: { id: string; code: string | null; name: string };
}

export interface AttendanceDay {
  id: string;
  date: string;
  employee: EmployeeRef;
  firstIn: string | null;
  lastOut: string | null;
  /** True when the punch fell on the day AFTER the row's date (overnight shift). */
  firstInNextDay: boolean;
  lastOutNextDay: boolean;
  workedMinutes: number;
  workedLabel: string;
  lateMinutes: number;
  earlyOutMinutes: number;
  punchCount: number;
  status: string;
  statusLabel: string;
  /** True when any of the day's punches came from the portal clock. */
  remote: boolean;
  /** PENDING | APPROVED | REJECTED for a remote day; null when not one. */
  wfhReviewState: string | null;
  /**
   * The arrangement that made this a work-from-home day, clocked or not.
   *
   * Non-null with `remote: false` is the case that matters: the employee was
   * expected to work from home and never clocked in. The status stays honest,
   * and this is what lets the row explain itself instead of showing a bare
   * absence for somebody reliable.
   */
  wfhArrangementId: string | null;
  /** Convenience for the UI: an arrangement covered this day. */
  wfhExpected: boolean;
  /** The approved leave behind an ON_LEAVE or leave-driven HALF_DAY row. */
  leaveRequestId: string | null;
  /**
   * Approved leave covered this day, but the employee came in and worked it.
   * The status is the clock-ins' own verdict, `leaveRequestId` still names the
   * leave, and the day was given back to the leave balance.
   */
  leaveSetAside: boolean;
  /** The working hours the day was judged against, "HH:mm"; null when none. */
  shift: { start: string; end: string } | null;
}

export interface AttendancePunch {
  id: string;
  deviceSerial: string;
  deviceName: string | null;
  /** The terminal's id, so a clock-in can link to the machine that recorded it. */
  deviceId: string;
  deviceUserId: string;
  punchedAt: string;
  localTime: string;
  localDate: string;
  direction: string | null;
  verifyMethod: string | null;
  source: string;
  /** Null when the device user is not mapped. Drives the "unmapped" badge. */
  employee: EmployeeRef | null;
  /**
   * The name the TERMINAL has for this id, on unmapped rows only.
   *
   * Never a substitute for `employee`: it is device free text, and mapping is a
   * payroll decision. It is here so an unidentified punch can be recognised
   * instead of read as a bare number.
   */
  deviceUserName: string | null;
  rawLine: string;
  /**
   * Short-lived signed link to the face capture, or null when the terminal
   * sent none (and for manual entries). It expires, so it is only good for the
   * page that fetched it — never persist it.
   */
  photoUrl: string | null;
}

/** A day with no rollup row: nothing was computed, which is NOT the same as absent. */
export interface MonthlyNoData {
  date: string;
  status: "NO_DATA";
  statusLabel: string;
  isFuture: boolean;
}

export type MonthlyDay = AttendanceDay | MonthlyNoData;

export function isNoData(day: MonthlyDay): day is MonthlyNoData {
  return day.status === "NO_DATA";
}

export interface MonthlyResponse {
  employee: { id: string; employeeNumber: string; fullName: string };
  year: number;
  month: number;
  days: MonthlyDay[];
  totals: {
    presentDays: number;
    lateDays: number;
    absentDays: number;
    onLeaveDays: number;
    holidayDays: number;
    weekendDays: number;
    totalWorkedMinutes: number;
    totalLateMinutes: number;
  };
}

export interface AttendanceStats {
  range: ResolvedRange;
  totals: {
    expectedDays: number;
    presentDays: number;
    lateDays: number;
    absentDays: number;
    onLeaveDays: number;
    attendanceRate: number | null;
    totalWorkedMinutes: number;
    averageWorkedMinutes: number;
    totalLateMinutes: number;
  };
  daily: Array<{
    date: string;
    present: number;
    late: number;
    absent: number;
    onLeave: number;
    attendanceRate: number | null;
    workedMinutes: number;
  }>;
  statusBreakdown: Array<{ status: string; label: string; count: number }>;
  /** Single-day ranges only: first clock-ins by time of day. */
  arrivals: ArrivalsHistogram | null;
}

export interface ArrivalsHistogram {
  bucketMinutes: number;
  /** The most common shift start, "HH:mm". */
  scheduleStart: string | null;
  graceMinutes: number | null;
  buckets: Array<{ start: string; count: number }>;
}

export interface DepartmentSummary {
  departmentId: string | null;
  departmentCode: string;
  departmentName: string;
  employeeCount: number;
  expectedDays: number;
  presentDays: number;
  lateDays: number;
  absentDays: number;
  onLeaveDays: number;
  attendanceRate: number | null;
  totalWorkedMinutes: number;
  averageWorkedMinutes: number;
}

// ---------------------------------------------------------------- devices

export type DeviceHealth = "online" | "stale" | "offline" | "never-seen";

export interface AttendanceDevice {
  id: string;
  serialNumber: string;
  name: string | null;
  location: string | null;
  status: "PENDING" | "APPROVED" | "DISABLED";
  timezoneOffset: number;
  health: DeviceHealth;
  /**
   * The per-company row where in-app punches (corrections, the web clock) hang.
   * No hardware corresponds to it, so it is kept out of terminal counts.
   */
  isVirtual: boolean;
  firstSeenAt: string;
  lastSeenAt: string | null;
  /** Refused calls while pending or disabled. Distinguishes new from misbehaving. */
  rejectedRequestCount: number;
  approvedAt: string | null;
  approvedBy: { id: string; name: string } | null;
  mappedUserCount: number;
  deviceInfo: Record<string, unknown> | null;
  createdAt: string;
}

export interface DeviceUserMapping {
  id: string;
  deviceUserId: string;
  isActive: boolean;
  label: string | null;
  /**
   * When this person's biometric templates were last queued for this terminal.
   *
   * Null means either that a push is still waiting for templates to arrive from
   * another terminal, or that nobody ever pushed because they enrolled here in
   * person — the two are told apart by whether a push created the mapping.
   */
  bioPushedAt: string | null;
  device: { id: string; serialNumber: string; name: string | null };
  employee: {
    id: string;
    employeeNumber: string;
    fullName: string;
    department: { id: string; code: string; name: string } | null;
  };
  /**
   * What the SERVER holds for this person — what a push to another terminal
   * can send — not what this terminal holds.
   */
  biometricsOnFile: { fingerprints: number; face: boolean };
  createdAt: string;
}

/** A device user producing punches with no mapping — the admin's work queue. */
export interface UnmappedDeviceUser {
  deviceId: string;
  deviceSerial: string;
  deviceName: string | null;
  deviceUserId: string;
  /**
   * The name enrolled on the TERMINAL, when a sync has told us.
   *
   * Evidence, not an answer: terminal names are free text and sometimes shared, so
   * this narrows the search rather than deciding it.
   */
  deviceUserName: string | null;
  /** Most recent punch photo for this enrolment id, presigned and short-lived. */
  photoUrl: string | null;
  punchCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface CreateMappingResult {
  mapping: DeviceUserMapping;
  /** Historical punches attached by this mapping. Worth surfacing — it is the point. */
  backfilledPunches: number;
  recomputedDays: number;
}

/** What became of the biometric half of a push, in one word. */
export type BiometricPushOutcome = "pushed" | "fetching" | "none" | "incompatible";

/**
 * What became of one biometric type: queued, on file but for an algorithm the
 * terminal does not run, or not on file at all.
 */
export type BiometricTypeOutcome = "sent" | "incompatible" | "none";

export interface BiometricPushDetail {
  fingerprints: BiometricTypeOutcome;
  face: BiometricTypeOutcome;
  fingerprintsSent: number;
  faceSent: number;
}

export interface PushEmployeeResult {
  mapping: DeviceUserMapping;
  biometrics: BiometricPushOutcome;
  /** Per-type account of what was on file when the push ran. */
  biometricDetail: BiometricPushDetail;
  commandsQueued: number;
  /**
   * Serial the templates are being re-read from, whenever the person is on
   * another approved terminal. Anything that read finds that was not on file
   * follows automatically.
   */
  fetchingFrom: string | null;
}

// ---------------------------------------------------------------- schedules

export interface WorkSchedule {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  graceMinutes: number;
  minMinutesFullDay: number;
  minMinutesHalfDay: number;
  weekendDays: number[];
  isDefault: boolean;
  isArchived: boolean;
  employeeCount: number;
  departmentCount: number;
  createdAt: string;
}

export interface Holiday {
  id: string;
  date: string;
  name: string;
  isRecurring: boolean;
  createdAt: string;
}

export const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
